import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { LOGO, IMAGES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Seo } from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";

const C = {
  fr: { back: "Retour à l'accueil", title: "Connexion", subtitle: "Accédez à votre espace en toute sécurité.",
    email: "Email", password: "Mot de passe", submit: "Se connecter", submitting: "Connexion…",
    noAccount: "Pas encore de compte ?", create: "Créer un compte", success: "Connexion réussie !",
    sideTitle: "Bienvenue chez Work & Travel Expert", sideText: "Une seule plateforme sécurisée pour suivre votre dossier, déposer vos documents et gérer vos réservations.",
    hq: "Siège social : Floride 🇺🇸 (États-Unis)", destLabel: "Destinations principales",
    aus: "Australie", ausText: "Visa · Emploi · Accompagnement", can: "Canada", canText: "Visa · Emploi · Accompagnement",
    change: "Changer d'espace",
    spaces: { client: "Espace Client", staff: "Espace Collaborateur", admin: "Espace Administrateur" } },
  en: { back: "Back to home", title: "Log in", subtitle: "Access your area securely.",
    email: "Email", password: "Password", submit: "Log in", submitting: "Logging in…",
    noAccount: "No account yet?", create: "Create an account", success: "Login successful!",
    sideTitle: "Welcome to Work & Travel Expert", sideText: "A single secure platform to track your file, upload your documents and manage your bookings.",
    hq: "Head office: Florida 🇺🇸 (USA)", destLabel: "Main destinations",
    aus: "Australia", ausText: "Visa · Jobs · Support", can: "Canada", canText: "Visa · Jobs · Support",
    change: "Change area",
    spaces: { client: "Client area", staff: "Collaborator area", admin: "Administrator area" } },
};

const DestCard = ({ flag, title, text }) => (
  <div className="flex items-center gap-3 rounded-xl border border-white/15 bg-white/[0.06] backdrop-blur-sm px-4 py-3">
    <span className="text-2xl leading-none">{flag}</span>
    <div className="min-w-0">
      <p className="text-white font-medium text-sm">{title}</p>
      <p className="text-white/60 text-xs truncate">{text}</p>
    </div>
  </div>
);

export default function Login() {
  const { lang } = useLang();
  const c = C[lang];
  const [searchParams] = useSearchParams();
  const espace = searchParams.get("espace");
  const spaceLabel = c.spaces[espace];
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(c.success);
      navigate(["admin", "staff"].includes(user.role) ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(apiErrorMessage(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen grid lg:grid-cols-2">
      <Seo title="Connexion — Espace client | Work & Travel Expert" description="Connectez-vous à votre espace Work & Travel Expert pour suivre votre dossier, déposer vos documents et gérer vos réservations." path="/connexion" noindex />
      {/* LEFT — prestige Miami panel (desktop) */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={IMAGES.miami} alt="Miami, Floride" className="absolute inset-0 w-full h-full object-cover scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 xl:p-16 text-white">
          <div className="inline-flex items-center gap-2 self-start rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold">
            <MapPin size={13} /> {c.hq}
          </div>
          <h2 className="font-heading text-4xl xl:text-5xl mt-6 leading-tight">{c.sideTitle}</h2>
          <p className="mt-4 text-white/75 max-w-md leading-relaxed">{c.sideText}</p>
          <p className="mt-8 text-xs uppercase tracking-[0.2em] text-gold">{c.destLabel}</p>
          <div className="mt-3 grid grid-cols-2 gap-3 max-w-md">
            <DestCard flag="🇦🇺" title={c.aus} text={c.ausText} />
            <DestCard flag="🇨🇦" title={c.can} text={c.canText} />
          </div>
        </div>
      </div>

      {/* RIGHT — login card */}
      <div className="relative flex flex-col justify-center items-center px-5 sm:px-8 py-12 bg-[#F3F4F6]">
        {/* mobile background accent */}
        <div className="lg:hidden absolute inset-x-0 top-0 h-40 bg-navy" />
        <div className="relative w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 text-sm text-white lg:text-muted-foreground hover:text-gold transition-colors mb-6"><ArrowLeft size={16} /> {c.back}</Link>

          <div className="bg-white rounded-2xl shadow-xl border border-border/60 p-8 sm:p-10">
            <div className="flex justify-center">
              <div className="bg-white rounded-full h-20 w-20 flex items-center justify-center shadow-md ring-1 ring-gold/30 p-1 -mt-16">
                <img src={LOGO} alt="Work & Travel Expert" className="h-full w-full object-contain rounded-full" />
              </div>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl text-navy text-center mt-5">{c.title}</h1>
            {spaceLabel ? (
              <div className="mt-3 flex flex-col items-center gap-1.5">
                <span data-testid="login-space-badge" className="inline-flex items-center rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-[0.15em] text-gold font-medium">{spaceLabel}</span>
                <Link to="/acces" data-testid="login-change-space" className="text-xs text-muted-foreground hover:text-gold transition-colors">{c.change}</Link>
              </div>
            ) : (
              <p className="text-muted-foreground text-center mt-2 text-sm">{c.subtitle}</p>
            )}

            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label className="text-navy text-sm">{c.email}</Label>
                <Input required type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 rounded-lg h-12" placeholder="vous@email.com" />
              </div>
              <div>
                <Label className="text-navy text-sm">{c.password}</Label>
                <Input required type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 rounded-lg h-12" placeholder="••••••••" />
              </div>
              <button type="submit" disabled={loading} data-testid="login-submit"
                className="group w-full flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-white py-3.5 rounded-lg font-medium transition-all duration-200 hover:shadow-lg hover:shadow-gold/30 disabled:opacity-60">
                {loading ? <><Loader2 size={17} className="animate-spin" /> {c.submitting}</> : <>{c.submit} <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" /></>}
              </button>
            </form>
            <p className="mt-6 text-sm text-muted-foreground text-center">{c.noAccount} <Link to="/inscription" data-testid="go-register" className="text-gold font-medium hover:underline">{c.create}</Link></p>
          </div>

          {/* mobile HQ + destinations */}
          <div className="lg:hidden mt-6">
            <div className="flex items-center justify-center gap-2 text-xs text-navy">
              <MapPin size={13} className="text-gold" /> {c.hq}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2.5">
                <span className="text-xl leading-none">🇦🇺</span>
                <div className="min-w-0"><p className="text-navy font-medium text-sm">{c.aus}</p><p className="text-muted-foreground text-[11px] truncate">{c.ausText}</p></div>
              </div>
              <div className="flex items-center gap-2 rounded-xl border border-border bg-white px-3 py-2.5">
                <span className="text-xl leading-none">🇨🇦</span>
                <div className="min-w-0"><p className="text-navy font-medium text-sm">{c.can}</p><p className="text-muted-foreground text-[11px] truncate">{c.canText}</p></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
